import sum from 'lodash/sum';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { createSlice, Dispatch } from '@reduxjs/toolkit';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
import { IProductState, ICheckoutCartItem } from 'src/types/product';

// ----------------------------------------------------------------------

const initialState: IProductState = {
  products: [],
  product: null,
  checkout: {
    activeStep: 0,
    cart: [],
    subTotal: 0,
    total: 0,
    discount: 0,
    shipping: 0,
    billing: null,
    totalItems: 0,
  },
  productsStatus: {
    loading: false,
    empty: false,
    error: null,
  },
  productStatus: {
    loading: false,
    error: null,
  },
};

const slice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // GET PRODUCTS
    getProductsStart(state) {
      state.productsStatus.loading = true;
      state.productsStatus.empty = false;
      state.productsStatus.error = null;
    },
    getProductsFailure(state, action) {
      state.productsStatus.loading = false;
      state.productsStatus.empty = false;
      state.productsStatus.error = action.payload;
    },
    getProductsSuccess(state, action) {
      const products = action.payload;

      state.products = products;

      state.productsStatus.loading = false;
      state.productsStatus.empty = !products.length;
      state.productsStatus.error = null;
    },

    // GET PRODUCT
    getProductStart(state) {
      state.productStatus.loading = true;
      state.productStatus.error = null;
    },
    getProductFailure(state, action) {
      state.productStatus.loading = false;
      state.productStatus.error = action.payload;
    },
    getProductSuccess(state, action) {
      const product = action.payload;

      state.product = product;

      state.productStatus.loading = false;
      state.productStatus.error = null;
    },

    // CHECKOUT
    getCart(state, action) {
      const cart: ICheckoutCartItem[] = action.payload;

      // Ensure qty = quantity for all incoming cart items
      const cartWithSyncedQty = cart.map((product) => ({
        ...product,
        qty: product.quantity || 1,
      }));

      const totalItems = sum(cartWithSyncedQty.map((product) => product.quantity));
      const subTotal = sum(cartWithSyncedQty.map((product) => product.price * product.quantity));

      state.checkout.cart = cart;
      state.checkout.discount = state.checkout.discount || 0;
      state.checkout.shipping = state.checkout.shipping || 0;
      state.checkout.billing = state.checkout.billing || null;
      state.checkout.subTotal = subTotal;
      state.checkout.total = subTotal - state.checkout.discount;
      state.checkout.totalItems = totalItems;
    },

    addToCart(state, action) {
      const newProduct = action.payload;

      const cartEmpty = !state.checkout.cart.length;

      if (cartEmpty) {
        // Assign no = 1 for first item
        const productWithNo = { ...newProduct, no: 1, qty: newProduct.quantity || 1 };
        state.checkout.cart = [...state.checkout.cart, productWithNo];
      } else {
        const existingProduct = state.checkout.cart.find((product) => product.id === newProduct.id);

        if (existingProduct) {
          // Update existing product quantity
          state.checkout.cart = state.checkout.cart.map((product) => {
            const existProduct = product.id === newProduct.id;

            if (existProduct) {
              const newQuantity = product.quantity + 1;
              return {
                ...product,
                colors: uniq([...product.colors, ...newProduct.colors]),
                quantity: newQuantity,
                qty: newQuantity, // Make qty same as quantity
                totalCost: product.cost ? product.cost * newQuantity : product.totalCost,
                totalPrice: product.price * newQuantity,
                amount: product.price * newQuantity - (product.discount || 0),
                subTotal: product.price * newQuantity,
              };
            }

            return product;
          });
        } else {
          // Add new product with next available no
          const maxNo = Math.max(...state.checkout.cart.map((item) => item.no || 0));
          const productWithNo = { ...newProduct, no: maxNo + 1, qty: newProduct.quantity || 1 };
          state.checkout.cart = [...state.checkout.cart, productWithNo];
        }
      }

      state.checkout.cart = uniqBy([...state.checkout.cart], 'id');

      // Recalculate totals
      const totalItems = sum(state.checkout.cart.map((product) => product.quantity));
      const subTotal = sum(state.checkout.cart.map((product) => product.price * product.quantity));

      state.checkout.totalItems = totalItems;
      state.checkout.subTotal = subTotal;
      state.checkout.total = subTotal - state.checkout.discount + state.checkout.shipping;
    },

    deleteCart(state, action) {
      const updateCart = state.checkout.cart.filter((product) => product.id !== action.payload);

      // Reassign no field to maintain sequential order
      const cartWithUpdatedNo = updateCart.map((product, index) => ({
        ...product,
        no: index + 1,
        qty: product.quantity,
      }));

      state.checkout.cart = cartWithUpdatedNo;

      // Recalculate totals
      const totalItems = sum(cartWithUpdatedNo.map((product) => product.quantity));
      const subTotal = sum(cartWithUpdatedNo.map((product) => product.price * product.quantity));

      state.checkout.totalItems = totalItems;
      state.checkout.subTotal = subTotal;
      state.checkout.total = subTotal - state.checkout.discount + state.checkout.shipping;
    },

    resetCart(state) {
      state.checkout.cart = [];
      state.checkout.billing = null;
      state.checkout.activeStep = 0;
      state.checkout.total = 0;
      state.checkout.subTotal = 0;
      state.checkout.discount = 0;
      state.checkout.shipping = 0;
      state.checkout.totalItems = 0;
    },

    backStep(state) {
      state.checkout.activeStep -= 1;
    },

    nextStep(state) {
      state.checkout.activeStep += 1;
    },

    gotoStep(state, action) {
      state.checkout.activeStep = action.payload;
    },

    increaseQuantity(state, action) {
      const productId = action.payload;

      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          const newQuantity = product.quantity + 1;
          return {
            ...product,
            quantity: newQuantity,
            qty: newQuantity, // Make qty same as quantity
            totalCost: product.cost ? product.cost * newQuantity : product.totalCost,
            totalPrice: product.price * newQuantity,
            amount: product.price * newQuantity - (product.discount || 0),
            subTotal: product.price * newQuantity,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;

      // Recalculate totals
      const totalItems = sum(updateCart.map((product) => product.quantity));
      const subTotal = sum(updateCart.map((product) => product.price * product.quantity));

      state.checkout.totalItems = totalItems;
      state.checkout.subTotal = subTotal;
      state.checkout.total = subTotal - state.checkout.discount + state.checkout.shipping;
    },

    decreaseQuantity(state, action) {
      const productId = action.payload;

      const updateCart = state.checkout.cart.map((product) => {
        if (product.id === productId) {
          const newQuantity = product.quantity - 1;
          return {
            ...product,
            quantity: newQuantity,
            qty: newQuantity, // Make qty same as quantity
            totalCost: product.cost ? product.cost * newQuantity : product.totalCost,
            totalPrice: product.price * newQuantity,
            amount: product.price * newQuantity - (product.discount || 0),
            subTotal: product.price * newQuantity,
          };
        }
        return product;
      });

      state.checkout.cart = updateCart;

      // Recalculate totals
      const totalItems = sum(updateCart.map((product) => product.quantity));
      const subTotal = sum(updateCart.map((product) => product.price * product.quantity));

      state.checkout.totalItems = totalItems;
      state.checkout.subTotal = subTotal;
      state.checkout.total = subTotal - state.checkout.discount + state.checkout.shipping;
    },

    createBilling(state, action) {
      state.checkout.billing = action.payload;
    },

    applyDiscount(state, action) {
      const discount = action.payload;

      state.checkout.discount = discount;
      state.checkout.total = state.checkout.subTotal - discount;
    },

    applyShipping(state, action) {
      const shipping = action.payload;

      state.checkout.shipping = shipping;
      state.checkout.total = state.checkout.subTotal - state.checkout.discount + shipping;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  getCart,
  addToCart,
  resetCart,
  gotoStep,
  backStep,
  nextStep,
  deleteCart,
  createBilling,
  applyShipping,
  applyDiscount,
  increaseQuantity,
  decreaseQuantity,
} = slice.actions;

// ----------------------------------------------------------------------

export function getProducts() {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.getProductsStart());
    try {
      const response = await axios.get(API_ENDPOINTS.product.list);
      dispatch(slice.actions.getProductsSuccess(response.data.products));
    } catch (error) {
      dispatch(slice.actions.getProductsFailure(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getProduct(productId: string) {
  return async (dispatch: Dispatch) => {
    dispatch(slice.actions.getProductStart());
    try {
      const response = await axios.get(API_ENDPOINTS.product.details, {
        params: {
          productId,
        },
      });
      dispatch(slice.actions.getProductSuccess(response.data.product));
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.getProductFailure(error));
    }
  };
}
